import type {
  SignUpRequest,
  UserInquiriesRequest,
  UserInquiriesResponse,
} from '@/types';

import React from 'react';

import {
  ReferralCodeForm,
  ReferralMobileForm,
  ReferredUser,
} from './_components';

import {
  Box,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from '@/components';
import { useTranslation, useUserInquiries } from '@/hooks';
import { standaloneToast } from '@/utils';

type ReferralFormProps = {
  goNext: ({
    parent_id,
    registration_type,
  }: {
    parent_id?: number;
    registration_type?: SignUpRequest['registration_type'];
  }) => void;
  csrfToken: SignUpRequest['token'];
  parentId?: number;
  registerType?: SignUpRequest['registration_type'];
  hasReferralCode: boolean;
};

const ReferralForm: React.FC<ReferralFormProps> = ({
  goNext,
  parentId,
  registerType,
  csrfToken,
  hasReferralCode,
}) => {
  const { t } = useTranslation('pages.signUp.form.referralForm');

  const [status, setStatus] = React.useState<{
    state: 'GET_PARENT' | 'PARENT_FOUND';
    registerType: SignUpRequest['registration_type'];
  }>({
    state: 'GET_PARENT',
    registerType: 'liacode',
  });
  const [parent, setParent] = React.useState<UserInquiriesResponse | null>(
    null
  );

  const { mutateAsync: getInquiries, isPending: inquiriesLoading } =
    useUserInquiries();

  const actionOnResponse = ({
    response,
    type,
  }: {
    response: UserInquiriesResponse;
    type: ReferralFormProps['registerType'];
  }) => {
    if (response?.id) {
      setParent(response);
      setStatus({ state: 'PARENT_FOUND', registerType: type || 'liacode' });
    } else {
      standaloneToast({
        status: 'error',
        title: t('userNotFound'),
      });
    }
  };

  const getInquiriesHandler = (
    data: UserInquiriesRequest,
    type: SignUpRequest['registration_type']
  ) => {
    void getInquiries({ ...data, token: csrfToken }).then((response) => {
      actionOnResponse({ response, type });
    });
  };

  React.useEffect(() => {
    if (parentId && registerType) {
      void getInquiriesHandler(
        { user_id: parentId.toString(), user_type: 'agent' },
        registerType
      );
    } else {
      setStatus({
        state: 'GET_PARENT',
        registerType: 'liacode',
      });
    }
  }, []);

  const activeStep = {
    GET_PARENT: (
      <Tabs
        isLazy
        w="100%"
        flexGrow={1}
        height={'100%'}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        <Text
          fontSize={{ base: 'md', lg: 'xl' }}
          w="full"
          fontWeight="medium"
          textAlign="center"
        >
          {t('referHint')}
        </Text>
        <TabList
          mt={{ base: '50px', md: '80px' }}
          w="fit-content"
          borderBottomColor="transparent"
        >
          <Tab
            paddingInline="0"
            me={8}
            sx={{
              _selected: {
                color: 'primary.500',
                borderBottomColor: 'primary.500',
                borderBottomWidth: '2px',
              },
            }}
          >
            {t('referralMobile')}
          </Tab>
          <Tab
            paddingInline="0"
            sx={{
              _selected: {
                color: 'primary.500',
                borderBottomColor: 'primary.500',
                borderBottomWidth: '2px',
              },
            }}
          >
            {t('referralCode')}
          </Tab>
        </TabList>
        <TabPanels
          height="100%"
          flexGrow={1}
          display="flex"
          flexDirection="column"
        >
          <TabPanel
            sx={{
              padding: 0,
              height: '100%',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              paddingTop="16px"
              display="flex"
              flexDirection="column"
              height="100%"
              flexGrow={1}
            >
              <ReferralMobileForm
                onNext={(data) =>
                  void getInquiriesHandler(
                    { ...data, user_type: 'agent' },
                    'agent_mobile'
                  )
                }
                getInquiriesLoading={inquiriesLoading}
              />
            </Box>
          </TabPanel>
          <TabPanel
            sx={{
              padding: 0,
              height: '100%',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box
              paddingTop="16px"
              display="flex"
              flexDirection="column"
              height="100%"
              flexGrow={1}
            >
              <ReferralCodeForm
                onNext={(data) =>
                  void getInquiriesHandler(
                    { user_id: data.user_id.toString(), user_type: 'agent' },
                    'agent_id'
                  )
                }
                getInquiriesLoading={inquiriesLoading}
              />
            </Box>
          </TabPanel>
        </TabPanels>
      </Tabs>
    ),
    PARENT_FOUND: parent ? (
      <ReferredUser
        goNext={() =>
          goNext({
            parent_id: parseInt(parent.id),
            registration_type: status.registerType,
          })
        }
        goBack={() =>
          setStatus({ state: 'GET_PARENT', registerType: 'liacode' })
        }
        hasReferralCode={hasReferralCode}
        parent={parent}
      />
    ) : (
      ''
    ),
  };

  return (
    <Stack flex={1} w="100%" h="100%" align="flex-start" gap={4}>
      {activeStep[status.state]}
    </Stack>
  );
};
export default ReferralForm;
